import type { RecordingStatus } from "@cuekards/core/recording";
import { RECORDING_PHASE_LABEL, formatElapsed } from "@cuekards/core/recording";
import {
  REHEARSE_TITLE,
  rehearseBlocked,
  rehearseLabel,
} from "@cuekards/core/speech";
import { useEffect, useState } from "react";
import type { CaptureKind } from "@cuekards/core/media-devices";
import { PauseIcon, RecordIcon, SettingsIcon, StopIcon } from "./icons.js";
import { transportButtons } from "./transport-buttons.js";
import { useFollowerState } from "./use-follower-state.js";
import { useLiveElapsed } from "./use-live-elapsed.js";

/**
 * Whether the user has switched the microphone off (web-ui ticket 06).
 *
 * Its own subscription rather than a prop, for the reason `DevicePicker` has one:
 * the device state is main's, it changes without a render of anything above this,
 * and threading it down through `AppShell` would make every screen that never asks
 * about a device re-render when one is toggled. Two listeners on one event cost a
 * function call each.
 *
 * **Off, rather than absent.** A machine with no microphone reports none switched
 * off, which is right: "there is no microphone" is a different sentence, and not
 * one the rehearse control is in a position to say.
 */
function useMicOff(): boolean {
  const [off, setOff] = useState<CaptureKind[]>([]);

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getDeviceState().then((state) => {
      if (!cancelled) setOff(state.off);
    });
    const stop = window.cuekards.onDeviceStateChanged((state) => setOff(state.off));
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  return off.includes("audioinput");
}
/**
 * The bottom bar (ticket 12, FR-43): record, pause/resume, stop, the gear, and
 * what the engine is doing right now.
 *
 * Every button is the same call the matching shortcut makes, onto the same
 * controller behind the contract — there is one recording state, and this is a
 * view onto it, so the two routes can never disagree about what is rolling.
 *
 * No key is named on any of them, deliberately. Every one of these is rebindable
 * on both hosts now (one-window ticket 06), and the screen that lists them is one
 * click away — three hints that have to be kept true would buy less than they
 * cost on the row that has to stay readable at a glance mid-take.
 *
 * **Icons rather than labels** (one-window-polish ticket 04). The words are still
 * here, in `aria-label` and `title`: nothing is lost to a screen reader or to a
 * hover, and the row survives being the only chrome on the screen. Which button
 * says what is {@link transportButtons}, tested at that seam.
 *
 * **The row is the bottom of the window and its icons are centred**
 * (one-window-polish ticket 05). Which is why the icons are a group of their own
 * and the phase sits beside them in a track of its own: centring a row that
 * *contains* a timecode would shunt the buttons left by half a digit every time
 * the clock ticked past a wider one. The two message lines are above the row
 * rather than below it, and absent when there is nothing to say, so the bar is
 * one row tall the rest of the time.
 *
 * **Rehearse is here now**, in the left track that ticket 05 left empty so a
 * growing timecode could not shunt the icons.
 * `FollowPanel` used to argue the opposite — "putting it beside Record would
 * invite pressing it instead" — and that warning is about a fourth square in the
 * row of squares, a control the hand reaches for by position. A borderless text
 * link at the far edge of a 1fr track is not one, and the bar it saves was a whole
 * bordered box for two escape hatches.
 */
export function Transport({
  status,
  onOpenSettings,
}: {
  status: RecordingStatus;
  /** Show the settings, or hide them again — the gear toggles, so the button
   * that opened the modal is the button that closes it. The shell holds the
   * open/closed state; this only says the gear was pressed. */
  onOpenSettings: () => void;
}): JSX.Element {
  const { phase } = status;
  const busy = phase === "starting" || phase === "stopping";
  const { record, stop } = transportButtons(phase);
  const follower = useFollowerState();
  const micOff = useMicOff();
  const blocked = rehearseBlocked(follower, phase, micOff);

  return (
    <section className="transport">
      {/* The take is unrepeatable, so a failure is stated in full rather than
        * reduced to a red dot — including where the footage still is when the
        * remux is what failed. */}
      {status.error && (
        <p className="transport-error" role="alert">
          {status.error}
        </p>
      )}

      {/* And the follower's own failure, on the same line the bar already grows
        * when it has something to report — `FollowPanel` used to hold this and
        * argued it must be stated in full rather than reduced to a greyed
        * control, because without the recogniser cuekards is still a working
        * teleprompter (goal G5) and the reader needs to know that is what they
        * now have. Below the take's error, not above it: a take that failed is
        * the more urgent of the two, and only one of them costs footage. */}
      {follower.error && (
        <p className="transport-error" role="alert">
          {follower.error}
        </p>
      )}

      {/* The path in full, and a way to get to it. Telling someone where a file
        * is and leaving them to find it themselves is most of a bug when the
        * folder holds every take of the day — the button selects the take that
        * just finished, not merely the folder it is in. */}
      {status.lastOutput && !busy && (
        <p className="transport-saved" role="status">
          Saved to {status.lastOutput}{" "}
          <button
            type="button"
            className="link"
            onClick={() => window.cuekards.revealLastTake()}
            title="Show this file in Explorer"
          >
            Show in folder
          </button>
        </p>
      )}

      <div className="transport-row">
        {/* The left track, which held nothing but the centring of the icons until
          * now. A link and not a button: what `FollowPanel` warned against was a
          * fourth square in the row of squares. It records nothing, so it must
          * not look like the thing that does. */}
        <button
          type="button"
          className={`rehearse-link${follower.listening ? " listening" : ""}`}
          onClick={() => window.cuekards.toggleRehearsal()}
          disabled={blocked !== null}
          title={blocked ?? REHEARSE_TITLE}
        >
          {rehearseLabel(follower)}
        </button>

        <div className="transport-buttons">
          {/* One button, two jobs: it starts the take and then holds it. Keeping
            * the record and the pause in one place is what stops the row shifting
            * under a pointer that is already moving towards the stop. */}
          <button
            type="button"
            className="record"
            aria-label={record.label}
            title={record.label}
            onClick={() =>
              record.action === "start"
                ? window.cuekards.startRecording()
                : window.cuekards.togglePauseRecording()
            }
            disabled={record.disabled}
          >
            {record.icon === "record" ? <RecordIcon /> : <PauseIcon />}
          </button>
          {/* Stop during the countdown is a cancel, not a save — nothing has been
            * captured yet — so it says so rather than promising a file. The glyph
            * is the same either way: cancelling and stopping are one action on one
            * take, and only the outcome differs. */}
          <button
            type="button"
            aria-label={stop.label}
            title={stop.label}
            onClick={() => window.cuekards.stopRecording()}
            disabled={stop.disabled}
          >
            <StopIcon />
          </button>
          {/* The way into the settings, and the only one — the tab strip that
            * used to be the other way is gone. It is never disabled: nothing on
            * the settings screen is refused mid-take that is not refused on its
            * own screen, and a window whose only door is locked while it is
            * recording is worse than the screen behind it. */}
          <button
            type="button"
            className="settings-gear"
            aria-label="Settings"
            title="Settings"
            aria-haspopup="dialog"
            onClick={onOpenSettings}
          >
            <SettingsIcon />
          </button>
        </div>
        <TransportState status={status} />
      </div>
    </section>
  );
}

/** The phase in words, beside a live timecode while there is a take to time — or
 * the countdown's own number, since a `00:00` timecode during the countdown
 * would say nothing and hide the one figure that is moving. */
function TransportState({ status }: { status: RecordingStatus }): JSX.Element {
  const elapsed = useLiveElapsed(status);
  const detail =
    status.phase === "counting"
      ? status.countdown
      : status.phase === "idle"
        ? undefined
        : formatElapsed(elapsed);
  return (
    <span className={`transport-state phase-${status.phase}`} role="status">
      {RECORDING_PHASE_LABEL[status.phase]}
      {detail !== undefined && ` · ${detail}`}
    </span>
  );
}
